/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-04
 */

/** Orden que el dialogo de seleccion manda a su `loadDataFn` (Mantis #392). */
export interface OrdenDelDialogo {
  sortColumn?: string | null;
  sortDirection?: 'ASC' | 'DESC' | null;
}

/** Un criterio de orden del page-response: el servidor lee `column` (o `key`) y la direccion. */
export interface CriterioOrden {
  column: string;
  direction: 'ASC' | 'DESC';
}

/**
 * Traduce el orden elegido en el dialogo a los criterios del page-response.
 *
 * <p>Las columnas del dialogo a veces muestran un campo armado para la vista (p. ej.
 * `numeroDocumentoDisplay`); `mapa` dice que campo del servidor ordena cada una. Una columna que
 * no esta en el mapa se manda con su mismo nombre.</p>
 *
 * @param params parametros que recibe el `loadDataFn`
 * @param mapa columna del dialogo → campo del servidor
 * @param porDefecto orden cuando el usuario no eligio ninguno
 * @returns los criterios, o `porDefecto` (vacio si no se da)
 */
export function ordersByDelDialogo(
  params: OrdenDelDialogo | null | undefined,
  mapa: Record<string, string> = {},
  porDefecto: CriterioOrden[] = [],
): CriterioOrden[] {
  const columna = params?.sortColumn;
  if (!columna) return porDefecto;
  return [{ column: mapa[columna] ?? columna, direction: params?.sortDirection ?? 'ASC' }];
}

/**
 * Ordena en memoria la lista COMPLETA de un modal que pagina en el navegador, antes de cortar la
 * pagina. Asi el orden alcanza a todos los registros y no solo a los visibles.
 *
 * @param items lista completa (no se modifica)
 * @param params parametros que recibe el `loadDataFn`
 * @returns una copia ordenada, o la misma lista si no hay orden
 */
export function ordenarEnMemoria<T>(items: T[], params: OrdenDelDialogo | null | undefined): T[] {
  const columna = params?.sortColumn;
  if (!columna) return items;
  const signo = params?.sortDirection === 'DESC' ? -1 : 1;
  const valor = (x: T) => (x as Record<string, unknown>)[columna];
  return [...items].sort((a, b) => {
    const va = valor(a);
    const vb = valor(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * signo;
    return (
      String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * signo
    );
  });
}
